import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { addProjectionRequestDataToClientPackage } from '../../../utils/clientPackageUtils';
import { saveFileFromResponse } from '../../../utils/fileUtils';
import { showErrorToast } from '../../../utils/toastUtils';
import { useClient } from '../../client-api';
import { type ClientPackageDto, renderClientPackage } from '../../generated/client';
import { useProjectionSetup } from '../../projection-api/queries/use-projection-setup';
import { useGeneralProjectionSettings } from '../../projection-settings-api';

export const useDownloadPreview = (): UseMutationResult<AxiosResponse, AxiosError, ClientPackageDto> => {
    const { t } = useTranslation();

    const { data: generalSettings } = useGeneralProjectionSettings();
    const { data: client } = useClient();
    const projectionSettings = useProjectionSetup(generalSettings?.projectionPeriodYears, client);

    return useMutation({
        mutationFn: async (clientPackage) => {
            const updatedClientPackage = addProjectionRequestDataToClientPackage(clientPackage, projectionSettings.body);

            return (await renderClientPackage({ body: updatedClientPackage, responseType: 'arraybuffer' })) as AxiosResponse;
        },
        onSuccess: (response) => {
            const axiosResponse = response as AxiosResponse<string>;
            saveFileFromResponse(axiosResponse);
        },
        onError: (error) => showErrorToast({ error, userMessage: t('failureDocumentDownload') }),
    });
};
